// webphone.js imports setActiveSession from here; the two only call each
// other inside functions, so the cycle is harmless.
// eslint-disable-next-line import/no-cycle
import { captureNextOutboundSession, setForegroundSession } from './webphone';

let activeSession = null;
const listeners = {
  accepted: new Set(),
  terminated: new Set(),
  bye: new Set(),
};

export const setActiveSession = (session) => {
  if (activeSession) {
    try {
      activeSession.hangup();
    } catch (_) {}
  }
  activeSession = session;
  try {
    session.on('answered', () => {
      listeners.accepted.forEach((cb) => cb(session));
    });
    session.on('disposed', () => {
      listeners.terminated.forEach((cb) => cb());
      listeners.bye.forEach((cb) => cb());
      activeSession = null;
    });
  } catch (_) {}
};

export const getCurrentSession = () => activeSession;

// A warm transfer's consult leg (2026-10-29): the second session, to the
// specialist, while the client is on hold. Ending the call ends it too.
let consultSession = null;
export const getConsultSession = () => consultSession;
export const hangupConsult = async () => {
  const s = consultSession;
  consultSession = null;
  if (!s) return;
  try {
    if (s.state === 'answered') await s.hangup();
    else await s.cancel();
  } catch (_) {
    // Already gone (RingCentral tore it down): nothing to end.
  }
  try {
    s.dispose();
  } catch (_) {}
};

export const hangupActiveCall = () => {
  const session = activeSession;
  activeSession = null;
  Promise.resolve(hangupConsult()).catch(() => {});
  if (!session) return;
  // Close the local RTCPeerConnection + stop the mic/speaker tracks immediately
  // so audio cuts out the instant the user ends the call — don't wait on the SIP
  // round-trip. dispose() also emits 'disposed', which fires our terminated
  // listeners and the audio-element cleanup. (hangup() alone only sends a BYE and
  // leaves the local media open, so the caller keeps hearing the call.)
  try {
    session.dispose();
  } catch (_) {}
  // Tell the server to tear the call down (BYE) so the remote end drops too.
  // Fire-and-forget — the local side is already gone; swallow async failures.
  try {
    Promise.resolve(session.hangup()).catch(() => {});
  } catch (_) {}
};

export const onSessionEvent = (evt, cb) => {
  if (!listeners[evt]) listeners[evt] = new Set();
  listeners[evt].add(cb);
};

export const offSessionEvent = (evt, cb) => {
  if (!listeners[evt]) return;
  listeners[evt].delete(cb);
};

export const notifyAccepted = (session) => {
  listeners.accepted.forEach((cb) => cb(session));
};

export const isLocalAudioMuted = () => {
  const tracks = activeSession?.mediaStream?.getAudioTracks();
  if (!tracks || tracks.length === 0) return false;
  return tracks.every((t) => t.enabled === false);
};

export const muteLocalAudio = () => {
  try {
    activeSession?.mute();
  } catch (_) {}
};

export const unmuteLocalAudio = () => {
  try {
    activeSession?.unmute();
  } catch (_) {}
};

// BLIND transfer (2026-08-10): the callee is forwarded to the target
// (extension number or full number) and our leg ends. The SDK sends the
// REFER; the 'disposed' event fires our terminated listeners as usual.
export const transferActiveCall = async (target) => {
  if (!activeSession) throw new Error('No active call to transfer.');
  return activeSession.transfer(target);
};

// WARM transfer: the callee goes on hold and a CONSULT call dials the
// target. The SDK returns { complete, cancel, newSession }:
//   complete() — join callee + target, our leg drops off
//   cancel()   — hang up the consult and return to the callee (unhold)
// The SDK only resolves once the consult is answered at the SIP level,
// which RingCentral does at once, long before anyone picks up (2026-10-29,
// proven in the live frames). So the consult session is captured the moment
// it is dialed, and returned with the SDK's promise: { consultSession,
// result }. The caller reads "picked up" from the server (the specialist's
// own line), never from the SDK.
export const warmTransferActiveCall = async (target) => {
  if (!activeSession) throw new Error('No active call to transfer.');
  const client = activeSession;
  const captured = captureNextOutboundSession();
  const result = client.warmTransfer(target);
  // hold() runs first inside warmTransfer; if it throws, result rejects and
  // nothing was captured.
  const consult = await Promise.race([captured, result.then((r) => r.newSession)]);
  consultSession = consult;
  setForegroundSession(consult);
  // The held client must not hear the consult: their leg's mic is cut too.
  try {
    client.mute();
  } catch (_) {}
  return { consultSession: consult, result };
};

// Back from a consult: the consult leg is gone, the client comes off hold
// and hears the rep again. unmute only when the rep was not muted before.
export const returnToClient = async ({ wasMuted = false } = {}) => {
  const client = activeSession;
  await hangupConsult();
  if (!client) return;
  setForegroundSession(client);
  try {
    await client.unhold();
  } catch (_) {}
  if (!wasMuted) {
    try {
      client.unmute();
    } catch (_) {}
  }
};

export const startCallRecording = async () => {
  if (!activeSession) return null;
  try {
    return await activeSession.startRecording();
  } catch (e) {
    throw e;
  }
};

export const stopCallRecording = async () => {
  if (!activeSession) return null;
  try {
    return await activeSession.stopRecording();
  } catch (e) {
    throw e;
  }
};
